import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { UiBrandComponent, UiHeaderComponent } from '@vasta/frontend-ui-layout';
import { UiToastComponent } from '@vasta/frontend-ui-feedback';
import { UiConfirmDialogComponent } from '@vasta/frontend-ui-overlay';
import { ImpersonationBannerComponent } from './impersonation-banner/impersonation-banner.component';
import { TopbarUserComponent } from './topbar/topbar-user.component';
import { TenantLandingService } from './tenant-landing.service';

@Component({
  selector: 'cl-main-layout',
  imports: [
    UiBrandComponent,
    UiHeaderComponent,
    TopbarUserComponent,
    UiToastComponent,
    UiConfirmDialogComponent,
    ImpersonationBannerComponent,
    RouterLink,
  ],
  standalone: true,
  templateUrl: './main-layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MainLayoutComponent {
  private readonly router = inject(Router);
  private readonly landing = inject(TenantLandingService);

  readonly currentTenantId = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      startWith(null),
      map(() => {
        let route = this.router.routerState.snapshot.root;
        while (route) {
          if (route.params['tenantId']) return route.params['tenantId'];
          if (!route.firstChild) break;
          route = route.firstChild;
        }
        return '';
      }),
    ),
    { initialValue: '' },
  );

  /**
   * Where the lockup leads: the schedule of the workspace in the URL, or the
   * bare root when there is none yet — `tenantRedirectGuard` then picks the
   * first tenant, or the wizard for a user who has no tenant at all.
   */
  readonly brandLink = computed<readonly string[]>(() => {
    const tenantId = this.currentTenantId();
    return tenantId
      ? ['/', tenantId, 'scheduling', 'schedule']
      : ['/'];
  });

  constructor() {
    /**
     * The derived gate of the onboarding plan.
     *
     * A tenant with no operators or no shifts is put back into the wizard the
     * first time it is landed on, and otherwise left alone: the wizard is
     * resumed at the step that owes something, and from inside the workspace the
     * user moves freely — including the wizard's own way out.
     *
     * It runs here, on `NavigationEnd`, and not in a guard, because of where the
     * `x-tenant-id` header comes from: `tenantInterceptor` reads the active
     * route, so a check made *while* entering a tenant for the first time would
     * ask the API about nobody. After the landing the URL carries the tenant and
     * the question has an answer.
     *
     * Only `currentTenantId` is read reactively, so this fires when the workspace
     * changes — login, a switch from the topbar, a reload — and not on every
     * navigation within one.
     */
    effect(() => {
      const tenantId = this.currentTenantId();

      if (tenantId.length === 0) {
        return;
      }

      this.landing.divert(tenantId, this.router.url).subscribe((step) => {
        if (step) {
          void this.router.navigate(['/', tenantId, 'onboarding', step]);
        }
      });
    });
  }
}
